/** Shared visual tokens for PromptCrew design system. */

export const FONT_FAMILY =
  '"Geist", "Geist Sans", Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const colors = {
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  textMuted: '#94a3b8',
  cardBorder: '#E8ECF3',
  cardBorderHover: '#D5DCE8',
  description: '#64748B',
  sidebarSurface: '#F9F9F8',
  sidebarBorder: '#E8E8E6',
  contentSurface: '#ffffff',
  navText: '#1F2937',
  navTextHover: '#111827',
  navSectionLabel: '#64748B',
  navChromeIcon: '#374151',
  navHoverBg: 'rgba(0, 0, 0, 0.04)',
  navActiveBg: 'rgba(15, 23, 42, 0.055)',
};

export const pageTitleSx = {
  fontSize: { xs: '1.625rem', sm: '1.9375rem' },
  fontWeight: 650,
  letterSpacing: '-0.02em',
  lineHeight: 1.25,
  color: colors.textPrimary,
};

export const sectionTitleSx = {
  fontSize: { xs: '1.25rem', sm: '1.375rem' },
  fontWeight: 600,
  letterSpacing: '-0.015em',
  lineHeight: 1.3,
  color: colors.textPrimary,
};

/** Primary content section headers — AI Instructions, capability editor blocks. */
export const primarySectionHeaderSx = {
  fontWeight: 650,
  fontSize: '0.9375rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.35,
  color: 'text.primary',
};

/** Secondary section headers — Description, Knowledge, Projects, Capability Type, etc. */
export const secondarySectionHeaderSx = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: 1.45,
  color: 'text.secondary',
};

export const libraryCardSx = {
  border: `1px solid ${colors.cardBorder}`,
  borderRadius: 2.5,
  boxShadow: 'none',
  transition: 'border-color 180ms ease, box-shadow 180ms ease',
  '&:hover': {
    borderColor: colors.cardBorderHover,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
  },
};

export const librarySearchFieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 48,
    bgcolor: 'background.paper',
    borderRadius: 2,
    fontSize: '0.9375rem',
  },
  '& .MuiOutlinedInput-input': {
    py: 1.375,
  },
};

export const filterChipSx = (selected) => ({
  fontWeight: selected ? 600 : 500,
  fontSize: '0.8125rem',
  height: 30,
  borderRadius: 999,
  borderColor: selected ? 'rgba(29, 78, 216, 0.35)' : '#E2E8F0',
  bgcolor: selected ? 'rgba(29, 78, 216, 0.08)' : 'transparent',
  color: selected ? 'primary.main' : colors.textSecondary,
  '&:hover': {
    bgcolor: selected ? 'rgba(29, 78, 216, 0.12)' : 'rgba(15, 23, 42, 0.04)',
    borderColor: selected ? 'rgba(29, 78, 216, 0.45)' : '#CBD5E1',
  },
});

/** Scope tabs — Library, Community, Builder mode selector, etc. */
export const libraryTabSx = {
  minHeight: 40,
  minWidth: 'auto',
  px: { xs: 1.25, sm: 1.5 },
  py: 1,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  color: 'text.secondary',
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: 600,
  },
};

const CATEGORY_CHIP_PALETTES = {
  business: {
    bgcolor: 'rgba(100, 116, 139, 0.1)',
    borderColor: 'rgba(100, 116, 139, 0.22)',
    color: '#475569',
  },
  technical: {
    bgcolor: 'rgba(29, 78, 216, 0.08)',
    borderColor: 'rgba(29, 78, 216, 0.22)',
    color: '#1d4ed8',
  },
  writing: {
    bgcolor: 'rgba(124, 58, 237, 0.08)',
    borderColor: 'rgba(124, 58, 237, 0.22)',
    color: '#6d28d9',
  },
  creative: {
    bgcolor: 'rgba(249, 115, 22, 0.1)',
    borderColor: 'rgba(249, 115, 22, 0.24)',
    color: '#c2410c',
  },
};

export function getCategoryChipSx(category) {
  const key = String(category || '')
    .trim()
    .toLowerCase();
  const palette = CATEGORY_CHIP_PALETTES[key];
  return {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
    borderRadius: 999,
    maxWidth: { xs: '100%', sm: 160 },
    ...(palette || {
      bgcolor: 'rgba(15, 23, 42, 0.04)',
      borderColor: '#E2E8F0',
      color: colors.textSecondary,
    }),
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      px: 1.25,
    },
  };
}
